import React from 'react';
import { 
  XAxis, YAxis, 
  CartesianGrid, Tooltip, ResponsiveContainer,
  Area, AreaChart
} from 'recharts';
import { PortfolioGraphData, AllPortfoliosGraphData } from '@/types/portfolio';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface PortfolioChartProps {
  graphData: PortfolioGraphData | AllPortfoliosGraphData | null;
  loading?: boolean;
  title?: string;
}

const formatMoney = (value: number) => {
  if (value === null || value === undefined) {
    return '$0';
  }
  
  const absValue = Math.abs(value);
  
  if (absValue >= 1000000000) {
    return `$${(value / 1000000000).toFixed(1)}B`;
  } else if (absValue >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (absValue >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  } else if (absValue >= 1) {
    return `$${value.toLocaleString('en-US')}`;
  }
  
  return `$${value.toFixed(0)}`;
};

const formatPercentage = (value: number) => {
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
};

const PortfolioChart: React.FC<PortfolioChartProps> = ({ 
  graphData, 
  loading = false, 
  title = "Portfolio Performance" 
}) => {
  if (loading) {
    return (
      <Card variant="default" padding="none" className="bg-white dark:bg-[#0A0A0A] relative">
        <div className="p-6 pb-4">
          <h3 className="text-[#2D2923] dark:text-[#FDFDFD] text-[20px] leading-[28px] font-semibold tracking-[-0.2px] mb-4">
            {title}
          </h3>
        </div>
        <CardContent className="pt-0">
          <div className="w-full h-[300px] px-6 flex items-center justify-center">
            <div className="space-y-4 w-full">
              <Skeleton className="h-[300px] w-full" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!graphData || !graphData.monthly_values || graphData.monthly_values.length === 0) {
    return (
      <Card variant="default" padding="none" className="bg-white dark:bg-[#0A0A0A] relative">
        <div className="p-6 pb-4">
          <h3 className="text-[#2D2923] dark:text-[#FDFDFD] text-[20px] leading-[28px] font-semibold tracking-[-0.2px] mb-4">
            {title}
          </h3>
        </div>
        <CardContent className="pt-0">
          <div className="w-full h-[300px] px-6 flex items-center justify-center">
            <p className="text-[rgba(45,41,35,0.64)]">No chart data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = graphData.monthly_values.map(item => ({
    date: item.date,
    totalValue: item.total_value || 0,
    totalInvested: item.total_invested || 0,
    gainLoss: item.gain_loss_usd || 0,
    gainLossPercentage: item.gain_loss_percentage || 0
  }));

  return (
    <Card variant="default" padding="none" className="bg-white dark:bg-[#0A0A0A] relative">
      <div className="p-6 pb-4">
        <h3 className="text-[#2D2923] dark:text-[#FDFDFD] text-[20px] leading-[28px] font-semibold tracking-[-0.2px] mb-4">
          {title}
        </h3>
      </div>
      <CardContent className="pt-0">
        <div className="w-full h-[300px] px-6">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart 
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid 
                vertical={true} 
                horizontal={true} 
                stroke="rgba(45,41,35,0.12)"
                opacity={0.5}
              />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 11, fill: "rgba(45,41,35,0.64)" }}
                tickMargin={10}
                interval="preserveStartEnd"
                stroke="rgba(45,41,35,0.12)"
              />
              <YAxis 
                tickFormatter={formatMoney}
                tick={{ fontSize: 11, fill: "rgba(45,41,35,0.64)" }}
                width={80}
                stroke="rgba(45,41,35,0.12)"
              />
              
              <Tooltip 
                contentStyle={{
                  backgroundColor: "#ffffff",
                  border: "1px solid rgba(45,41,35,0.12)",
                  color: "#2d2923",
                  borderRadius: "8px"
                }}
                formatter={(value: number, name: string) => {
                  if (name === 'totalValue') {
                    return [formatMoney(value), 'Total Value'];
                  }
                  if (name === 'totalInvested') {
                    return [formatMoney(value), 'Total Invested'];
                  }
                  if (name === 'gainLoss') {
                    return [formatMoney(value), 'Gain/Loss'];
                  }
                  if (name === 'gainLossPercentage') {
                    return [formatPercentage(value), 'Gain/Loss %'];
                  }
                  return [value, name];
                }}
                labelFormatter={(label) => `Date: ${label}`}
              />
              
              <Area
                type="monotone"
                dataKey="totalValue"
                stroke="#c8ed03"
                fill="rgba(200,237,3,0.3)"
                fillOpacity={1}
                strokeWidth={2}
              />
              
              <Area
                type="monotone"
                dataKey="totalInvested"
                stroke="rgba(45,41,35,0.48)"
                fill="rgba(45,41,35,0.08)"
                fillOpacity={1}
                strokeWidth={1}
                strokeDasharray="5 5"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        
        <div className="mt-4 px-6 pb-6">
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: "#c8ed03" }} />
              <span className="text-sm text-[rgba(45,41,35,0.72)]">Total Value</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-sm" style={{
                backgroundColor: "rgba(45,41,35,0.48)",
                backgroundImage: 'repeating-linear-gradient(45deg, rgba(45,41,35,0.48), rgba(45,41,35,0.48) 2px, transparent 2px, transparent 4px)'
              }} />
              <span className="text-sm text-[rgba(45,41,35,0.72)]">Total Invested</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PortfolioChart;
